import { getCurrentLanguage } from "../shared/language";

function initScrollRevealSections() {
  const revealSections = document.querySelectorAll('[data-scroll-reveal="true"]');

  if (!revealSections.length) return;

  const desktopMQ = window.matchMedia("(min-width: 768px)");
  const canAnimateWithGSAP = !!(window.gsap && window.ScrollTrigger);
  const supportsOverflowClip = !!window.CSS?.supports?.("overflow", "clip");
  const MIN_DESKTOP_REVEAL_OVERFLOW = 8;
  const DESKTOP_REVEAL_BUFFER_RATIO = 0.1;
  const DESKTOP_REVEAL_MIN_DISTANCE_RATIO = 0.35;
  const MIN_REVEAL_VIEWPORTS = 2;

  if (canAnimateWithGSAP && window.gsap.registerPlugin) {
    window.gsap.registerPlugin(window.ScrollTrigger);
  }

  revealSections.forEach((root) => {
    const triggerRoot = root.closest(".reveal__block-wrap") || root;
    const track =
      root.querySelector(".content__values--list, .swiper-wrapper") || root.firstElementChild;
    let tween = null;
    let refreshRafId = null;
    let refreshTimeoutId = null;

    if (!track) return;

    const clearTriggerRootSizing = () => {
      if (triggerRoot === root) return;

      triggerRoot.style.height = "auto";
      triggerRoot.style.minHeight = "0px";
    };

    const setTriggerRootSizing = (height) => {
      if (triggerRoot === root) return;

      const nextHeight = `${Math.ceil(height)}px`;
      triggerRoot.style.height = nextHeight;
      triggerRoot.style.minHeight = nextHeight;
    };

    const clearTween = () => {
      if (tween?.scrollTrigger) {
        tween.scrollTrigger.kill();
      }
      tween?.kill();
      tween = null;
    };

    const resetTrack = () => {
      clearTween();
      clearTriggerRootSizing();

      if (window.gsap) {
        window.gsap.set(track, { clearProps: "transform,willChange" });
      } else {
        track.style.transform = "";
        track.style.willChange = "";
      }
    };

    const applyMobileState = () => {
      resetTrack();

      root.style.overflowX = "auto";
      root.style.overflowY = "hidden";
      root.style.paddingBottom = "";
      root.style.scrollBehavior = "smooth";
    };

    const applyDesktopState = () => {
      resetTrack();

      root.scrollLeft = 0;
      root.style.overflowX = supportsOverflowClip ? "clip" : "hidden";
      root.style.overflowY = "visible";
      root.style.paddingBottom = "0.625em";
      root.style.scrollBehavior = "";

      if (!canAnimateWithGSAP) return;

      const getEndGap = () => {
        const fontSize = parseFloat(window.getComputedStyle(root).fontSize) || 16;
        return fontSize * 0.625;
      };
      const getBaseRevealHeight = () => Math.max(root.offsetHeight, window.innerHeight);
      const getMinRevealHeight = () => {
        const revealDistance = getRevealDistance();

        if (revealDistance <= 0) {
          return getBaseRevealHeight();
        }

        return Math.max(
          window.innerHeight * MIN_REVEAL_VIEWPORTS,
          getBaseRevealHeight() + revealDistance
        );
      };
      const getContentOverflow = () => Math.max(0, track.scrollWidth - root.clientWidth);
      const getMaxShift = () => Math.max(0, getContentOverflow() + getEndGap());
      const getRevealDistance = () => {
        const contentOverflow = getContentOverflow();

        if (contentOverflow <= MIN_DESKTOP_REVEAL_OVERFLOW) return 0;

        const minDistance = Math.round(window.innerHeight * DESKTOP_REVEAL_MIN_DISTANCE_RATIO);
        const bufferDistance = Math.round(window.innerHeight * DESKTOP_REVEAL_BUFFER_RATIO);

        return Math.max(Math.ceil(getMaxShift() + bufferDistance), minDistance);
      };
      const getAbsoluteTop = (el) => window.scrollY + el.getBoundingClientRect().top;
      const getTriggerDistance = () => {
        const triggerHeight = Math.max(
          triggerRoot.offsetHeight || triggerRoot.getBoundingClientRect().height || 0,
          0
        );
        const baseDistance = triggerHeight - window.innerHeight;

        return Math.max(baseDistance, Math.round(window.innerHeight * 0.5));
      };
      const getStickyScrollBounds = () => {
        const maxScroll =
          window.ScrollTrigger?.maxScroll(window) ??
          Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
        const rootTop = Math.round(getAbsoluteTop(root));
        const rootBottom = Math.round(rootTop + root.offsetHeight);
        const start = Math.max(
          0,
          Math.min(Math.max(rootTop, rootBottom - window.innerHeight), maxScroll)
        );
        const end = Math.max(
          start + 1,
          Math.min(
            Math.round(getAbsoluteTop(triggerRoot) + triggerRoot.offsetHeight - window.innerHeight),
            maxScroll
          )
        );

        return { start, end };
      };

      if (triggerRoot !== root) {
        setTriggerRootSizing(getMinRevealHeight());
      }

      if (getMaxShift() <= 0) return;

      window.gsap.set(track, { x: 0, willChange: "transform" });

      tween = window.gsap.to(track, {
        x: () => -getMaxShift(),
        ease: "none",
        overwrite: "auto",
        scrollTrigger: {
          trigger: triggerRoot,
          start: () => (triggerRoot !== root ? getStickyScrollBounds().start : "top top"),
          end: () =>
            triggerRoot !== root ? getStickyScrollBounds().end : "+=" + getTriggerDistance(),
          scrub: true,
          invalidateOnRefresh: true,
        },
      });
    };

    const scheduleRefresh = () => {
      if (!desktopMQ.matches || !canAnimateWithGSAP) return;

      if (refreshRafId !== null) {
        cancelAnimationFrame(refreshRafId);
      }

      if (refreshTimeoutId !== null) {
        clearTimeout(refreshTimeoutId);
      }

      refreshRafId = requestAnimationFrame(() => {
        refreshRafId = null;
        window.ScrollTrigger.refresh();

        refreshTimeoutId = window.setTimeout(() => {
          refreshTimeoutId = null;
          window.ScrollTrigger.refresh();
        }, 180);
      });
    };

    const syncMode = () => {
      if (root.swiper) {
        root.swiper.destroy(true, true);
      }

      if (desktopMQ.matches) {
        applyDesktopState();
      } else {
        applyMobileState();
      }

      scheduleRefresh();
    };

    syncMode();

    const onMediaChange = () => {
      syncMode();
    };

    const onWindowLoad = () => {
      if (!desktopMQ.matches || !canAnimateWithGSAP) return;
      scheduleRefresh();
      window.setTimeout(scheduleRefresh, 320);
    };

    if (desktopMQ.addEventListener) {
      desktopMQ.addEventListener("change", onMediaChange);
    } else if (desktopMQ.addListener) {
      desktopMQ.addListener(onMediaChange);
    }

    window.addEventListener("load", onWindowLoad, { once: true });

    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleRefresh).catch(() => {});
    }

    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => {
        scheduleRefresh();
      });

      ro.observe(root);
      ro.observe(track);
      if (triggerRoot !== root) {
        ro.observe(triggerRoot);
      }
    }
  });
}

(function () {
  document.addEventListener("DOMContentLoaded", () => {
    initScrollRevealSections();

    const formatSliders = document.querySelectorAll(".format__slider-wrap");
    if (formatSliders.length) {
      const pad2 = (n) => String(n).padStart(2, "0");
      const NEXT_LABELS = {
        en: "Next",
        uk: "Далі",
        pl: "Następnie",
        de: "Als Nächstes",
        fr: "Suivant",
      };
      const getNextLabel = () => NEXT_LABELS[getCurrentLanguage()] || NEXT_LABELS.en;

      formatSliders.forEach((sliderEl) => {
        const swiperFade = new Swiper(sliderEl, {
          effect: "fade",
          loop: true,
          fadeEffect: { crossFade: true },
          spaceBetween: 0,
          speed: 600,
        });

        const getTotal = () => swiperFade.slides.length;

        const getSlideTitle = (index) => {
          const slide = swiperFade.slides[index];
          if (!slide) return "";
          const titleEl = slide.querySelector(".format__content-title");
          return titleEl ? titleEl.textContent.trim() : "";
        };

        const updateUI = () => {
          const total = getTotal();
          const current = swiperFade.realIndex ?? swiperFade.activeIndex;
          const nextIndex = (current + 1) % total;
          const nextTitle = getSlideTitle(nextIndex);

          const activeCounters = sliderEl.querySelectorAll(".format__slider-counter--active");
          const totalCounters = sliderEl.querySelectorAll(".format__slider-counter--number");
          const previewTitles = sliderEl.querySelectorAll(".format__slider-preview .elem__types");

          activeCounters.forEach((el) => {
            el.textContent = pad2(current + 1);
          });

          totalCounters.forEach((el) => {
            el.textContent = pad2(total);
          });

          previewTitles.forEach((el) => {
            const nextLabel = getNextLabel();
            el.textContent = nextTitle ? `${nextLabel}: ${nextTitle}` : `${nextLabel}:`;
          });
        };

        updateUI();
        swiperFade.on("slideChange", updateUI);

        const clickHandler = (e) => {
          const prevBtn = e.target.closest(".format__slider-arrow--prev");
          const nextBtn = e.target.closest(".format__slider-arrow--next");

          if (prevBtn) {
            e.preventDefault();
            swiperFade.slidePrev();
          }
          if (nextBtn) {
            e.preventDefault();
            swiperFade.slideNext();
          }
        };

        sliderEl.addEventListener("click", clickHandler);
      });
    }
  });
})();
